import React, { useCallback, useEffect, useState } from 'react';
import FusePageCarded from '@fuse/core/FusePageCarded';
import FuseAnimate from '@fuse/core/FuseAnimate';
import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MicIcon from '@mui/icons-material/Mic';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';

import GlobalTabs from 'allGlobalComponents/GlobalTabs';
import ConversationTab from './tabs/ConversationTab';
import LeadsTab from './tabs/LeadsTab';
import SettingsTab from './tabs/SettingsTab';

const STORAGE_KEYS = {
	apiKey: 'voxlead_apikey',
	systemPrompt: 'voxlead_prompt',
	voice: 'voxlead_voice',
	interruptionsEnabled: 'voxlead_interruptions'
};

const SUPPORTED_VOICES = [
	'alloy',
	'ash',
	'ballad',
	'coral',
	'echo',
	'sage',
	'shimmer',
	'verse',
	'marin',
	'cedar'
];

const DEFAULT_SETTINGS = {
	apiKey: '',
	systemPrompt:
		'You are a friendly sales assistant. Engage in natural conversation, understand the user\'s needs, and when they share their contact details (name, email, phone, or company), use the generate_lead tool to capture them. Keep responses concise and conversational.',
	voice: 'alloy',
	interruptionsEnabled: true
};

const TABS = [
	{ id: 'conversation', label: 'Conversation', icon: MicIcon },
	{ id: 'leads', label: 'Leads', icon: PeopleIcon },
	{ id: 'settings', label: 'Settings', icon: SettingsIcon }
];

const useStyles = makeStyles(theme => ({
	contentContainer: {
		padding: theme.spacing(2),
		width: '100%'
	}
}));

function loadSettings() {
	if (typeof window === 'undefined' || !window.localStorage) return { ...DEFAULT_SETTINGS };
	try {
		const storedVoice = window.localStorage.getItem(STORAGE_KEYS.voice);
		const voice = SUPPORTED_VOICES.includes(storedVoice) ? storedVoice : DEFAULT_SETTINGS.voice;
		const storedInterruptions = window.localStorage.getItem(STORAGE_KEYS.interruptionsEnabled);
		const interruptionsEnabled = storedInterruptions === null ? DEFAULT_SETTINGS.interruptionsEnabled : storedInterruptions === 'true';
		return {
			apiKey: window.localStorage.getItem(STORAGE_KEYS.apiKey) || DEFAULT_SETTINGS.apiKey,
			systemPrompt:
				window.localStorage.getItem(STORAGE_KEYS.systemPrompt) || DEFAULT_SETTINGS.systemPrompt,
			voice,
			interruptionsEnabled
		};
	} catch (e) {
		return { ...DEFAULT_SETTINGS };
	}
}

function AICall() {
	const classes = useStyles();
	const [activeTab, setActiveTab] = useState('conversation');

	const [settings, setSettings] = useState(loadSettings);
	const [messages, setMessages] = useState([]);
	const [leads, setLeads] = useState([]);

	useEffect(() => {
		try {
			window.localStorage.setItem(STORAGE_KEYS.apiKey, settings.apiKey || '');
			window.localStorage.setItem(STORAGE_KEYS.systemPrompt, settings.systemPrompt || '');
			window.localStorage.setItem(STORAGE_KEYS.voice, settings.voice || DEFAULT_SETTINGS.voice);
			window.localStorage.setItem(STORAGE_KEYS.interruptionsEnabled, String(settings.interruptionsEnabled !== false));
		} catch (e) {
			// localStorage unavailable; ignore
		}
	}, [settings]);

	const updateSettings = useCallback(patch => {
		setSettings(prev => ({ ...prev, ...patch }));
	}, []);

	const addMessage = useCallback(message => {
		setMessages(prev => [
			...prev,
			{
				id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
				timestamp: new Date().toISOString(),
				...message
			}
		]);
	}, []);

	const clearMessages = useCallback(() => setMessages([]), []);

	const addLead = useCallback(leadData => {
		setLeads(prev => [
			{
				id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
				name: leadData.name || '',
				email: leadData.email || '',
				phone: leadData.phone || '',
				company: leadData.company || '',
				capturedAt: new Date().toISOString()
			},
			...prev
		]);
	}, []);

	const renderTabContent = () => {
		switch (activeTab) {
			case 'conversation':
				return (
					<ConversationTab
						settings={settings}
						messages={messages}
						addMessage={addMessage}
						clearMessages={clearMessages}
						addLead={addLead}
					/>
				);
			case 'leads':
				return <LeadsTab leads={leads} />;
			case 'settings':
				return <SettingsTab settings={settings} updateSettings={updateSettings} />;
			default:
				return (
					<ConversationTab
						settings={settings}
						messages={messages}
						addMessage={addMessage}
						clearMessages={clearMessages}
						addLead={addLead}
					/>
				);
		}
	};

	return (
		<FusePageCarded
			header={
				<div className="flex flex-1 w-full items-center justify-between flex-wrap">
					<div className="hidden sm:flex items-center">
						<FuseAnimate animation="transition.expandIn" delay={300}>
							<MicIcon fontSize="large" />
						</FuseAnimate>
						<FuseAnimate animation="transition.slideLeftIn" delay={300}>
							<Typography className="flex mx-0 sm:mx-12" variant="h6">
								<span style={{ fontSize: '15px' }}>Voice AI Agent</span>
							</Typography>
						</FuseAnimate>
					</div>
				</div>
			}
			content={
				<div className={classes.contentContainer}>
					<GlobalTabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />
					{renderTabContent()}
				</div>
			}
		/>
	);
}

export default AICall;
